import api from "@seller-hermes/utils/api";
import type {
  PortalMeResponse,
  PortalProfile,
  PortalSellerGroupContext,
} from "./portal.types";

const PORTAL_PROFILES: PortalProfile[] = ["LOG", "GO", "PAY"];

const isPortalProfile = (value?: string | null): value is PortalProfile =>
  Boolean(value && PORTAL_PROFILES.includes(value as PortalProfile));

const normalizePortalMe = (portalMe: PortalMeResponse): PortalMeResponse => ({
  ...portalMe,
  seller_groups: withUnavailableProductPlaceholders(
    portalMe.seller_groups
      .filter((context) => isPortalProfile(context.profile))
      .map((context) => ({
        ...context,
        status: context.status || "available",
        is_default:
          context.is_default ||
          portalMe.default_seller_group?.id === context.id,
      })),
  ),
});

const withUnavailableProductPlaceholders = (
  contexts: PortalSellerGroupContext[],
): PortalSellerGroupContext[] => {
  const profiles = new Set(contexts.map((context) => context.profile));
  const placeholders: PortalSellerGroupContext[] = [];

  if (!profiles.has("LOG")) {
    placeholders.push({
      id: "unavailable-log",
      name: "Ative o Abbiamo LOG",
      description:
        "Cotacao de frete, gestao de pedidos e integracao com transportadoras.",
      profile: "LOG",
      is_homologation: false,
      status: "unavailable",
      status_label: "Produto ainda nao disponivel",
      metric_label: "Para contas habilitadas",
    });
  }

  if (!profiles.has("GO")) {
    placeholders.push({
      id: "unavailable-go",
      name: "Ative o Abbiamo GO",
      description:
        "Rotas, motoristas e execucao de entregas com frota propria.",
      profile: "GO",
      is_homologation: false,
      status: "unavailable",
      status_label: "Produto ainda nao disponivel",
      metric_label: "Para contas habilitadas",
    });
  }

  if (!profiles.has("PAY")) {
    placeholders.push({
      id: "unavailable-pay",
      name: "Abbiamo PAY",
      description:
        "Pague motoristas, transportadoras e a Abbiamo em um so lugar.",
      profile: "PAY",
      is_homologation: false,
      status: "unavailable",
      status_label: "Produto ainda nao disponivel",
      metric_label: "Simplifique seus pagamentos",
    });
  }

  return [...contexts, ...placeholders];
};

// Collapses homologation contexts into their production counterparts so the
// portal page shows one card per profile with an inline homologation link.
// Collapsing only happens when there is exactly one production context for a
// given profile — two or more prod contexts of the same profile are shown
// individually so the user can distinguish them.
// getMe() keeps the full list intact for context-bootstrap session selection.
const normalizePortalMeForPortal = (
  portalMe: PortalMeResponse,
): PortalMeResponse => {
  const hmlByProfile = new Map(
    portalMe.seller_groups
      .filter((c) => c.is_homologation)
      .map((c) => [c.profile, c]),
  );

  if (!hmlByProfile.size) return portalMe;

  const prodCountByProfile = new Map<string, number>();
  for (const c of portalMe.seller_groups) {
    if (!c.is_homologation) {
      prodCountByProfile.set(
        c.profile,
        (prodCountByProfile.get(c.profile) ?? 0) + 1,
      );
    }
  }

  const collapsedHmlIds = new Set<string>();

  const merged = portalMe.seller_groups
    .filter((c) => !c.is_homologation)
    .map((c) => {
      const hml = hmlByProfile.get(c.profile);
      const isOnlyProd = (prodCountByProfile.get(c.profile) ?? 0) === 1;
      if (hml && isOnlyProd) {
        collapsedHmlIds.add(hml.id);
        return {
          ...c,
          homologation_label: "Abrir em homologacao",
          homologation_id: hml.id,
          homologation_user_seller_group_id: hml.user_seller_group_id,
          homologation_products: hml.products,
        };
      }
      return c;
    });

  // Keep homologation contexts that were NOT collapsed (their profile has
  // multiple prod environments, so they appear as standalone cards).
  const uncollapsedHml = portalMe.seller_groups.filter(
    (c) => c.is_homologation && !collapsedHmlIds.has(c.id),
  );

  return { ...portalMe, seller_groups: [...merged, ...uncollapsedHml] };
};

export class PortalService {
  static async getMe() {
    const res = await api.get<PortalMeResponse>("/portal/me");
    return normalizePortalMe(res.data);
  }

  static async getMeForPortal() {
    return normalizePortalMeForPortal(await PortalService.getMe());
  }

  static async updateDefaultSellerGroup(userSellerGroupId: string) {
    const res = await api.patch("/v2/users/me/default-seller-group", {
      user_seller_group_id: userSellerGroupId,
    });
    return res.data;
  }
}
